// Composite renderer — produces a downloadable / clipboard-able PNG of
// the player's drawing. Mirrors the EndScreenModal MarcSlot visual: a
// gold-framed egg portrait with a circular marc reference tag pinned
// upper-right.
//
// Inputs come from whatever surface the player is looking at (speedrun
// active marc, creative selection, end-screen recap), so the caller
// passes raw values rather than reading any global state.

import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  DEFAULT_EGG_TRANSFORM,
  FRAME_OUTER_HEIGHT,
  FRAME_OUTER_WIDTH,
  FRAME_SLICE_LEFT,
  FRAME_SLICE_TOP,
} from "./constants";
import {
  applyLighting,
  computeNaturalEllipse,
  drawEggTransformed,
} from "./eggLighting";
import { eggImageUrl, marcImageUrl } from "./paths";
import { renderStroke } from "./strokeRender";
import {
  dropShadowFilter,
  tintFilter,
} from "./components/DrawingArea/DrawingArea";

// Page-bg cream (--color-background = orange-100). The orange canvas-bg
// stays inside the gold frame; the area around the frame is the calmer
// neutral so the portrait reads as the focal point.
const BG_COLOR = "#ffedd5";
const CANVAS_BG_COLOR = "#fb923c";
const MARC_BORDER_COLOR = "#fed7aa"; // --color-chrome (orange-200)
// Matches ReferencePhoto Frame's box-shadow inset: a thin dark ring just
// inside the chrome border. Black at 25% — gives the framed-photo look.
const MARC_INSET_RING = "rgba(0, 0, 0, 0.25)";
// Outer halo: matches the second non-inset box-shadow on
// ReferencePhoto.Frame (var(--color-gold-shadow), #713f12). Without
// this the chrome's outer edge blends into the cream page bg where the
// card overhangs the egg frame.
const MARC_OUTER_HALO = "#713f12";
const SHADOW_COLOR = "rgba(0, 0, 0, 0.22)";
const MARC_BORDER_RADIUS = 4;

// Marc card sits tucked into the upper-right of the egg frame: its
// bottom-left corner anchored 15% down from the frame's top edge and 15%
// left of the frame's right edge. The card extends up-and-right from
// there, overhanging the frame in both axes.
const MARC_CARD_FRACTION = 0.3;
const MARC_CARD_ANCHOR_FROM_TOP = 0.2;
const MARC_CARD_ANCHOR_FROM_RIGHT = 0.25;
const MARC_CARD_BORDER = 10;

const MARC_CARD_W = Math.round(FRAME_OUTER_WIDTH * MARC_CARD_FRACTION);
const MARC_CARD_H = Math.round(MARC_CARD_W * (CANVAS_HEIGHT / CANVAS_WIDTH));

// Asymmetric padding — top + right need enough room for the marc card
// overhang above the frame top and past the frame right edge.
const PAD_TOP = Math.max(
  80,
  MARC_CARD_H - Math.round(FRAME_OUTER_HEIGHT * MARC_CARD_ANCHOR_FROM_TOP) + 40,
);
const PAD_RIGHT = Math.max(
  80,
  MARC_CARD_W -
    Math.round(FRAME_OUTER_WIDTH * MARC_CARD_ANCHOR_FROM_RIGHT) +
    40,
);
const PAD_LEFT = 80;
const PAD_BOTTOM = 80;
const COMPOSITE_WIDTH = FRAME_OUTER_WIDTH + PAD_LEFT + PAD_RIGHT;
const COMPOSITE_HEIGHT = FRAME_OUTER_HEIGHT + PAD_TOP + PAD_BOTTOM;

const FRAME_LEFT = PAD_LEFT;
const FRAME_TOP = PAD_TOP;
const FRAME_INTERIOR_LEFT = FRAME_LEFT + FRAME_SLICE_LEFT;
const FRAME_INTERIOR_TOP = FRAME_TOP + FRAME_SLICE_TOP;

const MARC_CARD_BL_X =
  FRAME_LEFT +
  Math.round(FRAME_OUTER_WIDTH * (1 - MARC_CARD_ANCHOR_FROM_RIGHT));
const MARC_CARD_BL_Y =
  FRAME_TOP + Math.round(FRAME_OUTER_HEIGHT * MARC_CARD_ANCHOR_FROM_TOP);
const MARC_CARD_X = MARC_CARD_BL_X;
const MARC_CARD_Y = MARC_CARD_BL_Y - MARC_CARD_H;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) =>
      reject(new Error(`load failed: ${src} (${e?.message ?? e})`));
    img.src = src;
  });
}

// Renders the egg + relight into its own CANVAS_WIDTH × CANVAS_HEIGHT
// buffer. Pipeline mirrors useEggLighting on the live app: contain-fit
// the egg with eggTransform baked in, then analytic relight from the
// natural-ellipse silhouette + lights array. Returned canvas has a
// transparent background so the caller can drop-shadow around the egg
// silhouette and tint via ctx.filter at composite time.
function buildLitEggCanvas(eggImg, eggTransform, lights) {
  const c = document.createElement("canvas");
  c.width = CANVAS_WIDTH;
  c.height = CANVAS_HEIGHT;
  const ctx = c.getContext("2d");
  drawEggTransformed({
    destCtx: ctx,
    sourceImg: eggImg,
    eggTransform: eggTransform ?? DEFAULT_EGG_TRANSFORM,
    frameW: CANVAS_WIDTH,
    frameH: CANVAS_HEIGHT,
  });
  if (lights && lights.length > 0) {
    const naturalEllipse = computeNaturalEllipse(eggImg);
    applyLighting({
      destCtx: ctx,
      naturalEllipse,
      eggTransform: eggTransform ?? DEFAULT_EGG_TRANSFORM,
      frameW: CANVAS_WIDTH,
      frameH: CANVAS_HEIGHT,
      lights,
    });
  }
  return c;
}

function buildStrokesCanvas(strokes, palette) {
  const c = document.createElement("canvas");
  c.width = CANVAS_WIDTH;
  c.height = CANVAS_HEIGHT;
  const ctx = c.getContext("2d");
  for (const s of strokes ?? []) {
    renderStroke(ctx, s, palette);
  }
  return c;
}

function pathRoundedRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, w, h, rr);
  } else {
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y, x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x, y + h, rr);
    ctx.arcTo(x, y + h, x, y, rr);
    ctx.arcTo(x, y, x + w, y, rr);
    ctx.closePath();
  }
}

// Replicates marcTransformToCss on a 2D context. Marc <img> is sized to
// the frame's interior dims, then translated by tx/ty (canvas-virtual px,
// scaled here by the inner box's mult), rotated, and scaled around its
// own center.
function drawMarcInFrame(ctx, marcImg, transform, innerW, innerH) {
  const { tx = 0, ty = 0, scale = 1, rot = 0 } = transform ?? {};
  const naturalW = marcImg.naturalWidth;
  const naturalH = marcImg.naturalHeight;
  const fit = Math.min(innerW / naturalW, innerH / naturalH);
  const drawW = naturalW * fit;
  const drawH = naturalH * fit;

  // marcTransform's tx/ty live in canvas-virtual px (CANVAS_WIDTH-scale).
  // Inner box is innerW × innerH, so scale tx/ty into the inner box's
  // coordinate space.
  const mult = innerW / CANVAS_WIDTH;

  ctx.save();
  ctx.translate(innerW / 2 + tx * mult, innerH / 2 + ty * mult);
  ctx.rotate((rot * Math.PI) / 180);
  ctx.scale(scale, scale);
  ctx.drawImage(marcImg, -drawW / 2, -drawH / 2, drawW, drawH);
  ctx.restore();
}

// Small chrome-bordered marc card pinned to upper-right of the egg
// frame. Mirrors ReferencePhoto.Frame: rounded chrome border +
// dark-inset ring just inside the chrome. Drop shadow gives the card
// lift so it reads as a sticker pinned ON the portrait.
function drawMarcCard(ctx, marcImg, marcTransform) {
  // Draw a slightly larger gold-shadow rect first; the chrome on top
  // leaves a 1px gold ring poking out around the perimeter — this is
  // the outer-halo half of ReferencePhoto.Frame's box-shadow stack
  // (the bit that gives the chrome a defined edge against the page bg).
  // The lift drop-shadow rides on this pass too so the shadow geometry
  // tracks the halo, not the chrome.
  ctx.save();
  ctx.shadowColor = SHADOW_COLOR;
  ctx.shadowBlur = 22;
  ctx.shadowOffsetY = 8;
  ctx.fillStyle = MARC_OUTER_HALO;
  pathRoundedRect(
    ctx,
    MARC_CARD_X - 1,
    MARC_CARD_Y - 1,
    MARC_CARD_W + 2,
    MARC_CARD_H + 2,
    MARC_BORDER_RADIUS + 1,
  );
  ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.fillStyle = MARC_BORDER_COLOR;
  pathRoundedRect(
    ctx,
    MARC_CARD_X,
    MARC_CARD_Y,
    MARC_CARD_W,
    MARC_CARD_H,
    MARC_BORDER_RADIUS,
  );
  ctx.fill();
  ctx.restore();

  const innerX = MARC_CARD_X + MARC_CARD_BORDER;
  const innerY = MARC_CARD_Y + MARC_CARD_BORDER;
  const innerW = MARC_CARD_W - MARC_CARD_BORDER * 2;
  const innerH = MARC_CARD_H - MARC_CARD_BORDER * 2;

  ctx.save();
  ctx.fillStyle = CANVAS_BG_COLOR;
  ctx.fillRect(innerX, innerY, innerW, innerH);
  ctx.beginPath();
  ctx.rect(innerX, innerY, innerW, innerH);
  ctx.clip();
  ctx.translate(innerX, innerY);
  drawMarcInFrame(ctx, marcImg, marcTransform, innerW, innerH);
  ctx.restore();

  // Dark inset ring — matches ReferencePhoto.Frame's box-shadow inset
  // (0 0 1px 2px rgba(0,0,0,0.25) inset). Stroked half-pixel-inside the
  // chrome edge so the line lands fully visible.
  ctx.save();
  ctx.strokeStyle = MARC_INSET_RING;
  ctx.lineWidth = 2;
  ctx.strokeRect(innerX + 1, innerY + 1, innerW - 2, innerH - 2);
  ctx.restore();
}

export async function renderComposite({
  marcId,
  eggId,
  strokes,
  palette,
  eggTransform,
  marcTransform,
  lights,
  warmth,
  redshift,
}) {
  const [eggImg, marcImg, frameImg] = await Promise.all([
    loadImage(eggImageUrl(eggId)),
    loadImage(marcImageUrl(marcId)),
    loadImage("/frame.png"),
  ]);

  const canvas = document.createElement("canvas");
  canvas.width = COMPOSITE_WIDTH;
  canvas.height = COMPOSITE_HEIGHT;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = BG_COLOR;
  ctx.fillRect(0, 0, COMPOSITE_WIDTH, COMPOSITE_HEIGHT);

  // Frame interior orange — sits below the lit egg + strokes.
  ctx.fillStyle = CANVAS_BG_COLOR;
  ctx.fillRect(
    FRAME_INTERIOR_LEFT,
    FRAME_INTERIOR_TOP,
    CANVAS_WIDTH,
    CANVAS_HEIGHT,
  );

  const litEgg = buildLitEggCanvas(eggImg, eggTransform, lights);

  // Lit egg goes down with tint (warmth/redshift) + light-direction drop
  // shadow, mirroring DrawingArea's CSS filter stack on EggCanvas.
  ctx.save();
  ctx.filter = `${tintFilter(warmth ?? 0, redshift ?? 0)} ${dropShadowFilter(lights)}`;
  ctx.drawImage(litEgg, FRAME_INTERIOR_LEFT, FRAME_INTERIOR_TOP);
  ctx.restore();

  // Strokes on top with no filter — they're the player's drawing colors
  // and shouldn't pick up the egg's warmth tint.
  const strokesCanvas = buildStrokesCanvas(strokes, palette);
  ctx.drawImage(strokesCanvas, FRAME_INTERIOR_LEFT, FRAME_INTERIOR_TOP);

  // Drop shadow on the gold frame chrome only — filter respects the PNG
  // alpha so the transparent interior doesn't tint a shadow underneath.
  ctx.save();
  ctx.filter = "drop-shadow(0 10px 22px rgba(0, 0, 0, 0.22))";
  ctx.drawImage(
    frameImg,
    FRAME_LEFT,
    FRAME_TOP,
    FRAME_OUTER_WIDTH,
    FRAME_OUTER_HEIGHT,
  );
  ctx.restore();

  drawMarcCard(ctx, marcImg, marcTransform);

  return canvas;
}

export function canvasToBlob(canvas, type = "image/png") {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, type);
  });
}

export async function downloadComposite(opts, filename) {
  const canvas = await renderComposite(opts);
  const blob = await canvasToBlob(canvas);
  if (!blob) return false;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename ?? `marc-${opts.marcId ?? "egg"}.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return true;
}

export function canCopyImageToClipboard() {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.clipboard?.write &&
    typeof ClipboardItem !== "undefined"
  );
}

export async function copyCompositeToClipboard(opts) {
  if (!canCopyImageToClipboard()) return false;
  const canvas = await renderComposite(opts);
  const blob = await canvasToBlob(canvas);
  if (!blob) return false;
  try {
    await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
    return true;
  } catch {
    return false;
  }
}
